// A small history-API router: enough for this console without pulling in a
// routing dependency. Patterns use ":name" segments, e.g. "/admin/developers/:id".
import { createContext, useContext, useEffect, useMemo, useState, type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from "react";

interface Loc {
  pathname: string;
  search: string;
}

const RouterCtx = createContext<Loc>({ pathname: "/", search: "" });
const EVENT = "gbe:navigate";

function current(): Loc {
  return { pathname: window.location.pathname, search: window.location.search };
}

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  if (to === window.location.pathname + window.location.search) return;
  if (opts.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  window.dispatchEvent(new Event(EVENT));
  window.scrollTo?.(0, 0);
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState<Loc>(current);
  useEffect(() => {
    const on = () => setLoc(current());
    window.addEventListener("popstate", on);
    window.addEventListener(EVENT, on);
    return () => {
      window.removeEventListener("popstate", on);
      window.removeEventListener(EVENT, on);
    };
  }, []);
  return <RouterCtx.Provider value={loc}>{children}</RouterCtx.Provider>;
}

export function useLocation(): Loc {
  return useContext(RouterCtx);
}

export function useSearchParam(name: string): [string, (v: string) => void] {
  const { pathname, search } = useLocation();
  const value = useMemo(() => new URLSearchParams(search).get(name) ?? "", [search, name]);
  const set = (v: string) => {
    const p = new URLSearchParams(search);
    if (v) p.set(name, v);
    else p.delete(name);
    const s = p.toString();
    navigate(pathname + (s ? `?${s}` : ""), { replace: true });
  };
  return [value, set];
}

/** Returns params when `path` matches `pattern`, else null. A trailing "/*" matches any rest. */
export function matchPath(pattern: string, path: string): Record<string, string> | null {
  const clean = (s: string) => s.replace(/\/+$/, "") || "/";
  const pp = clean(pattern).split("/");
  const xs = clean(path).split("/");
  const params: Record<string, string> = {};
  for (let i = 0; i < pp.length; i++) {
    const p = pp[i];
    if (p === "*") {
      params["*"] = xs.slice(i).join("/");
      return params;
    }
    const x = xs[i];
    if (x === undefined) return null;
    if (p.startsWith(":")) params[p.slice(1)] = decodeURIComponent(x);
    else if (p !== x) return null;
  }
  return xs.length === pp.length ? params : null;
}

export interface RouteDef {
  path: string;
  render: (params: Record<string, string>) => ReactNode;
}

export function Routes({ routes, fallback }: { routes: RouteDef[]; fallback: ReactNode }) {
  const { pathname } = useLocation();
  for (const r of routes) {
    const m = matchPath(r.path, pathname);
    if (m) return <>{r.render(m)}</>;
  }
  return <>{fallback}</>;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; exact?: boolean; replace?: boolean };

/** In-app link. Sets aria-current="page" when the location matches. */
export function Link({ to, exact, replace, onClick, children, ...rest }: LinkProps) {
  const { pathname } = useLocation();
  const path = to.split("?")[0];
  const active = exact ? pathname === path : pathname === path || pathname.startsWith(path + "/");
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || rest.target) return;
    e.preventDefault();
    navigate(to, { replace });
  };
  return (
    <a href={to} onClick={handle} aria-current={active ? "page" : undefined} {...rest}>
      {children}
    </a>
  );
}

export function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}
